import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, AlertCircle } from "lucide-react";

interface SummaryCardProps {
  path: "orthopedic" | "gi" | "other" | null;
  selectedRegions: string[];
  regionAnswers: Record<string, Record<string, string>>;
  giAnswers: {
    symptoms: string[];
    frequency: string;
    duration: string;
    triggers: string;
  };
  otherConcern: string;
  baseline: {
    painLevel: number;
    mobilityRating: number;
    duration: string;
    dailyImpact: string;
    giFrequency: string;
    giSeverity: number;
    giDuration: string;
  };
  baselineScore: number;
  onProceed: () => void;
  onReview: () => void;
}

const regionLabels: Record<string, string> = {
  "head-neck": "Head / Neck",
  "shoulder-left": "Left Shoulder",
  "shoulder-right": "Right Shoulder",
  "elbow-left": "Left Elbow",
  "elbow-right": "Right Elbow",
  "wrist-hand-left": "Left Wrist / Hand",
  "wrist-hand-right": "Right Wrist / Hand",
  "chest": "Chest",
  "upper-back": "Upper Back",
  "lower-back": "Lower Back",
  "abdomen": "Abdomen",
  "hip-left": "Left Hip",
  "hip-right": "Right Hip",
  "knee-left": "Left Knee",
  "knee-right": "Right Knee",
  "ankle-foot-left": "Left Ankle / Foot",
  "ankle-foot-right": "Right Ankle / Foot",
};

export function SummaryCard({
  path,
  selectedRegions,
  regionAnswers,
  giAnswers,
  otherConcern,
  baseline,
  baselineScore,
  onProceed,
  onReview,
}: SummaryCardProps) {
  const concernType =
    path === "orthopedic"
      ? "Orthopedic Recovery"
      : path === "gi"
      ? "Digestive & GI"
      : "Other Concern";

  return (
    <Card className="border-teal-200 dark:border-teal-800" data-testid="summary-card">
      <CardHeader>
        <div className="flex items-center gap-3 mb-2">
          <CardTitle className="text-2xl font-serif">Your Recovery Summary</CardTitle>
          <Badge variant="secondary">{concernType}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {path === "orthopedic" && (
          <>
            <div>
              <h4 className="font-semibold mb-2">Affected Areas</h4>
              <div className="flex flex-wrap gap-2">
                {selectedRegions.map((regionId) => (
                  <Badge key={regionId} variant="outline" className="bg-teal-50 dark:bg-teal-950/30">
                    {regionLabels[regionId] || regionId}
                  </Badge>
                ))}
              </div>
            </div>

            {Object.keys(regionAnswers).length > 0 && (
              <div>
                <h4 className="font-semibold mb-3">Key Symptoms</h4>
                <div className="space-y-3 text-sm">
                  {Object.entries(regionAnswers).map(([regionId, answers]) => (
                    <div key={regionId} className="p-3 bg-muted/50 rounded-lg">
                      <p className="font-medium mb-1 text-teal-700">
                        {regionLabels[regionId] || regionId}
                      </p>
                      <ul className="space-y-1 text-muted-foreground">
                        {Object.entries(answers).map(([question, answer]) => (
                          <li key={question}>
                            {question}: <span className="text-foreground">{answer}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {path === "gi" && (
          <>
            <div>
              <h4 className="font-semibold mb-2">Symptoms</h4>
              <div className="flex flex-wrap gap-2">
                {giAnswers.symptoms.map((symptom) => (
                  <Badge key={symptom} variant="outline" className="bg-teal-50 dark:bg-teal-950/30">
                    {symptom}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Frequency</p>
                <p className="font-medium">{giAnswers.frequency}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Duration</p>
                <p className="font-medium">{giAnswers.duration}</p>
              </div>
            </div>

            {giAnswers.triggers && (
              <div>
                <h4 className="font-semibold mb-1">Known Triggers</h4>
                <p className="text-sm text-muted-foreground">{giAnswers.triggers}</p>
              </div>
            )}
          </>
        )}

        {path === "other" && (
          <div>
            <h4 className="font-semibold mb-2">Your Concern</h4>
            <p className="text-sm text-muted-foreground leading-relaxed">{otherConcern}</p>
          </div>
        )}

        {path !== "other" && (
          <div className="p-4 bg-teal-50 dark:bg-teal-950/30 rounded-lg border border-teal-200 dark:border-teal-800">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-teal-900 dark:text-teal-100">
                Your Starting Point
              </h4>
              <span className="text-3xl font-serif font-semibold text-teal-700">
                {baselineScore}/100
              </span>
            </div>
            <div className="h-2 bg-teal-200 dark:bg-teal-900 rounded-full overflow-hidden">
              <div
                className="h-full bg-teal-700"
                style={{ width: `${baselineScore}%` }}
              />
            </div>
          </div>
        )}

        <div className="p-4 bg-muted/50 rounded-lg space-y-3">
          <h4 className="font-semibold">8-Week BPC-157 Recovery Course</h4>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {path === "orthopedic" &&
              "Based on your responses, an 8-week BPC-157 course may support your recovery by promoting tissue repair, reducing inflammation, and supporting joint health. This peptide therapy is designed to work alongside your body's natural healing processes."}
            {path === "gi" &&
              "Based on your responses, an 8-week BPC-157 course may support your recovery by promoting gut lining repair, reducing inflammation, and supporting digestive health. This peptide therapy is designed to work with your body's natural healing processes."}
            {path === "other" &&
              "An 8-week BPC-157 course may support your recovery goals. This peptide therapy is designed to promote healing and reduce inflammation."}
          </p>
        </div>

        <div className="p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg flex gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-amber-900 dark:text-amber-100 mb-1">
              This is an internal concept prototype
            </p>
            <p className="text-amber-800 dark:text-amber-200">
              Not medical advice. Any real treatment plan requires review and prescription by
              a licensed healthcare provider.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <Button
            onClick={onProceed}
            className="flex-1 bg-teal-700 hover:bg-teal-800 text-white border-teal-800"
            data-testid="button-proceed-to-confirm"
          >
            Looks good — proceed
          </Button>
          <Button
            onClick={onReview}
            variant="outline"
            className="flex-1"
            data-testid="button-review-answers"
          >
            <ArrowLeft className="mr-2 w-4 h-4" />
            Review my answers
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
